'use client';

import { debounce } from 'lodash';
import { CalendarIcon } from 'lucide-react';
import * as React from 'react';
import { DateRange, Matcher } from 'react-day-picker';
import { zhCN } from 'react-day-picker/locale';

import { Input } from 'src/components/input';
import { Calendar } from 'src/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from 'src/components/ui/popover';
import { useControl } from 'src/hooks';
import dayjs from 'src/lib/dayjs';
import { ymd } from 'src/lib/lang/time';
import { cn } from 'src/lib/utils';

export interface DateRangePickerProps {
  defaultValue?: [Date, Date];
  value?: [Date, Date];
  onChange?: (value: [Date, Date] | undefined) => void;
  placeholder?: string;
  className?: string;
  clearable?: boolean;
  captionLayout?: 'dropdown' | 'dropdown-years' | 'label' | 'dropdown-months' | undefined;
  disabled?: Matcher | Matcher[];
}

const DateRegex = /^\d{4}-\d{2}-\d{2}$/;

// 判断日期是否匹配 Matcher
function isDateDisabled(date: Date, matcher: Matcher | Matcher[] | undefined): boolean {
  if (!matcher) return false;

  // 处理数组形式的 Matcher
  if (Array.isArray(matcher)) {
    return matcher.some((m) => isDateDisabled(date, m));
  }

  // boolean 类型
  if (typeof matcher === 'boolean') {
    return matcher;
  }

  // 函数类型
  if (typeof matcher === 'function') {
    return matcher(date);
  }

  // Date 类型 - 禁用特定日期
  if (matcher instanceof Date) {
    return dayjs(date).isSame(dayjs(matcher), 'day');
  }

  // DateRange 类型 - { from, to }
  if (typeof matcher === 'object' && 'from' in matcher && 'to' in matcher) {
    const from = matcher.from;
    const to = matcher.to;
    if (from && to) {
      const d = dayjs(date);
      return (
        (d.isAfter(dayjs(from), 'day') || d.isSame(dayjs(from), 'day')) &&
        (d.isBefore(dayjs(to), 'day') || d.isSame(dayjs(to), 'day'))
      );
    }
    if (from) {
      const d = dayjs(date);
      return d.isAfter(dayjs(from), 'day') || d.isSame(dayjs(from), 'day');
    }
    if (to) {
      const d = dayjs(date);
      return d.isBefore(dayjs(to), 'day') || d.isSame(dayjs(to), 'day');
    }
  }

  // DateBefore 类型 - { before: Date }
  if (typeof matcher === 'object' && 'before' in matcher) {
    return dayjs(date).isBefore(dayjs(matcher.before), 'day');
  }

  // DateAfter 类型 - { after: Date }
  if (typeof matcher === 'object' && 'after' in matcher) {
    return dayjs(date).isAfter(dayjs(matcher.after), 'day');
  }

  // DayOfWeek 类型 - { dayOfWeek: number[] }
  if (typeof matcher === 'object' && 'dayOfWeek' in matcher) {
    const dayOfWeek = date.getDay(); // 0 (Sunday) to 6 (Saturday)
    return Array.isArray(matcher.dayOfWeek) && matcher.dayOfWeek.includes(dayOfWeek);
  }

  return false;
}

function toValidDate(str: string | undefined, disabled?: Matcher | Matcher[]): Date | undefined {
  if (!str) return undefined;
  const dateStr = str.trim();
  if (!DateRegex.test(dateStr)) return undefined;
  const date = dayjs(dateStr);
  if (!date.isValid()) return undefined;

  const dateObj = date.toDate();

  // 检查日期是否被禁用
  if (disabled && isDateDisabled(dateObj, disabled)) {
    return undefined;
  }

  return dateObj;
}

// 显示文本
function getDisplay(dateRange: [Date, Date] | undefined) {
  if (!dateRange) return '';
  const start = ymd(dateRange[0]);
  const end = ymd(dateRange[1]);
  if (start && end) return `${start} ~ ${end}`;
  return '';
}

// 判断 Input 是否应该被禁用
// 如果 disabled 是 boolean，直接返回
// 否则认为是 Calendar 的 Matcher，Input 不禁用
function isInputDisabled(disabled: Matcher | Matcher[] | undefined): boolean {
  return typeof disabled === 'boolean' ? disabled : false;
}

export function DateRangePicker({
  defaultValue,
  value,
  onChange,
  placeholder = '请选择日期范围',
  className,
  clearable = true,
  captionLayout = 'dropdown-years',
  disabled,
}: DateRangePickerProps) {
  const [open, setOpen] = React.useState(false);
  const [tempStart, setTempStart] = React.useState<Date | undefined>(undefined);
  const [tempEnd, setTempEnd] = React.useState<Date | undefined>(undefined);
  const [inputValue, setInputValue] = React.useState('');
  const [dateRange, setDateRange] = useControl(defaultValue, value, onChange);
  // 使用 ref 来跟踪清除操作，防止受控值覆盖清除状态
  const isClearingRef = React.useRef(false);

  // 将 [Date, Date] 转换为 Calendar 组件需要的 { from, to } 格式
  const calendarRange = React.useMemo<DateRange | undefined>(() => {
    // 如果有临时的日期范围，显示临时状态
    if (tempStart && tempEnd) {
      return {
        from: tempStart,
        to: tempEnd,
      };
    }
    // 如果只有临时的开始日期，只显示开始日期
    if (tempStart) {
      return {
        from: tempStart,
        to: undefined,
      };
    }
    // 否则显示当前选中的范围
    if (dateRange) {
      return {
        from: dateRange[0],
        to: dateRange[1],
      };
    }
    return undefined;
  }, [dateRange, tempStart, tempEnd]);

  // 同步 inputValue 和 display（仅在从外部改变时更新）
  React.useEffect(() => {
    // 如果正在清除，不更新 inputValue，保持为空
    if (isClearingRef.current) {
      isClearingRef.current = false;
      return;
    }
    setInputValue((old) => {
      const display = getDisplay(dateRange);
      if (old !== display) {
        return display;
      }
      // 如果没有变化，返回旧值，减少重新渲染
      return old;
    });
  }, [dateRange]);

  // 创建防抖版本的 parseAndUpdateTemp
  const debouncedParseAndUpdateTemp = React.useMemo(
    () =>
      debounce((input: string) => {
        const trimmedInput = input.trim();

        if (!trimmedInput) {
          // 如果输入为空，清空临时状态
          setTempStart(undefined);
          setTempEnd(undefined);
          return;
        }

        // 支持的格式：YYYY-MM-DD ~ YYYY-MM-DD
        const [startStr, endStr] = trimmedInput.split('~');
        const startDate = toValidDate(startStr, disabled);
        const endDate = toValidDate(endStr, disabled);

        if (!startDate) {
          // 开始日期如果没有，清空临时状态
          setTempStart(undefined);
          setTempEnd(undefined);
          return;
        }

        if (startDate) {
          setTempStart(startDate);
        }

        if (endDate && endDate > startDate) {
          setTempEnd(endDate);
        } else {
          setTempEnd(undefined);
        }
      }, 50),
    [disabled]
  );

  const handleSelect = (range: DateRange | undefined, trigger?: Date) => {
    if (!trigger) return;

    // 如果没有临时开始日期，设置开始日期
    if (!tempStart) {
      setTempStart(trigger);
      setTempEnd(undefined);
      return;
    }

    // 如果有开始日期，设置结束日期
    let start = tempStart;
    let end = trigger;

    // 确保开始日期在结束日期之前
    if (dayjs(start).isAfter(dayjs(end))) {
      [start, end] = [end, start];
    }

    setDateRange([start, end]);
    setTempStart(undefined);
    setTempEnd(undefined);
    setOpen(false); // 选择完成后关闭弹窗
    setInputValue(getDisplay([start, end])); // 立即更新 input 显示
  };

  const handleClear = () => {
    // 标记正在清除，防止 useEffect 覆盖 inputValue
    isClearingRef.current = true;
    // 立即清空 inputValue，确保 UI 立即更新
    setInputValue('');
    // 清空临时状态
    setTempStart(undefined);
    setTempEnd(undefined);
    // 调用 onChange 更新表单字段
    setDateRange(undefined);
  };

  // 处理输入框的值变化
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    debouncedParseAndUpdateTemp(value);
  };

  // 处理输入框失去焦点
  const handleInputBlur = () => {
    setTimeout(() => {
      // 如果 Popover 还是打开状态，说明焦点转移到了 Calendar
      // 此时不处理 input blur
      if (open) {
        return;
      }
      // 如果有完整的临时日期范围，提交到 dateRange
      if (tempStart && tempEnd) {
        setDateRange([tempStart, tempEnd]);
      } else if (!inputValue.trim()) {
        // 如果输入为空，清空所有状态
        setInputValue('');
        setDateRange(undefined);
      } else {
        // 还原到 display
        setInputValue(getDisplay(dateRange));
      }

      // 清空 temp 状态
      setTempStart(undefined);
      setTempEnd(undefined);
    }, 0);
  };

  // 处理回车键
  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setOpen(false);
      handleInputBlur();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
    }
  };

  const btn = (
    <span
      id="date-range-picker"
      role="button"
      tabIndex={-1}
      className="h-6 w-6 -mr-1 text-muted-foreground hover:text-accent-foreground flex items-center justify-center rounded-md transition hover:shadow-xs hover:bg-accent select-none outline-none"
      aria-label="Select date range"
    >
      <CalendarIcon className="size-4" />
      <span className="sr-only">Select date range</span>
    </span>
  );

  const shouldClear = clearable && !!inputValue;
  const inputDisabled = isInputDisabled(disabled);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger>
        <Input
          value={inputValue}
          placeholder={placeholder}
          suffix={shouldClear ? undefined : btn}
          disabled={inputDisabled}
          className={cn('pr-9', className)}
          clearable={shouldClear}
          onClear={handleClear}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onKeyDown={handleInputKeyDown}
        />
      </PopoverTrigger>
      <PopoverContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="w-auto overflow-hidden p-0"
        align="end"
        alignOffset={-8}
        sideOffset={10}
      >
        <Calendar
          mode="range"
          selected={calendarRange}
          onSelect={handleSelect}
          captionLayout={captionLayout}
          locale={zhCN}
          numberOfMonths={2}
          defaultMonth={dateRange?.[0]}
          disabled={disabled}
          classNames={{
            day: cn(
              'rounded-md',
              'hover:rounded-md',
              '[&>button]:hover:text-primary-foreground',
              '[&>button]:hover:rounded-md',
              // hover 样式：根据是否有 tempStart 设置不同的背景和文字颜色
              tempStart
                ? 'hover:bg-primary [&>button]:hover:bg-primary'
                : 'hover:bg-primary/60 [&>button]:hover:bg-primary/60',
              // tempStart 节点的样式：选择 button 元素上的 data-selected-single
              '[&>button[data-selected-single="true"]]:bg-primary/60',
              '[&>button[data-selected-single="true"]]:text-primary-foreground'
            ),
            outside: cn(
              'cursor-default',
              '[&>button]:cursor-default',
              '[&>button]:pointer-events-none',
              '[&>button]:text-transparent!',
              '[&>button]:bg-transparent!',
              'bg-transparent!'
            ),
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
